import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Modal, Btn } from "@/components/common";
import { Camera, FileText, Trash2, Download } from "lucide-react";

const MAX = 10 * 1024 * 1024;

export default function ReceiptsModal({ expense, onClose, onChanged }) {
  const [files, setFiles] = useState([]);
  const [urls, setUrls] = useState({});
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const eid = expense?.expense_id;

  const load = async () => {
    const r = await api.get(`/expenses/${eid}/attachments`);
    setFiles(r.data);
    const next = {};
    await Promise.all(r.data.map(async (f) => {
      try {
        const b = await api.get(`/attachments/${f.id}`, { responseType: "blob" });
        next[f.id] = URL.createObjectURL(b.data);
      } catch (e) { console.warn(`Anteprima allegato ${f.id} non disponibile`, e); }
    }));
    setUrls((old) => { Object.values(old).forEach((u) => URL.revokeObjectURL(u)); return next; });
  };
  useEffect(() => {
    if (!eid) return;
    load().catch(() => toast.error("Allegati non caricati"));
  }, [eid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { Object.values(urls).forEach((u) => URL.revokeObjectURL(u)); }, [urls]);

  const upload = async (list) => {
    const arr = Array.from(list || []);
    if (arr.some((f) => f.size > MAX)) { toast.error("Ogni file deve essere al massimo 10 MB"); return; }
    setBusy(true);
    try {
      for (const f of arr) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/expenses/${eid}/attachments`, fd);
      }
      toast.success(arr.length > 1 ? `${arr.length} allegati caricati` : "Allegato caricato");
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Caricamento non riuscito"); }
    setBusy(false);
    if (input.current) input.current.value = "";
  };
  const remove = async (f) => {
    if (!window.confirm(`Eliminare ${f.original_filename}?`)) return;
    try {
      await api.delete(`/attachments/${f.id}`);
    } catch (e) { toast.error("Eliminazione non riuscita"); }
    await load().catch(() => {}); onChanged?.();
  };

  return (
    <Modal open={!!expense} onClose={onClose} title={expense ? `Ricevute · ${expense.name}` : ""}>
      <div className="space-y-3" data-testid="receipts-modal">
        <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf" className="hidden" data-testid="receipts-input" onChange={(e) => upload(e.target.files)} />
        <Btn data-testid="receipts-add" onClick={() => input.current?.click()} disabled={busy}><Camera size={16} /> {busy ? "Caricamento…" : "Scatta o scegli foto / PDF"}</Btn>
        <p className="text-[11px] text-slate-400">Foto della bolletta o dello scontrino (JPG, PNG, WEBP, HEIC) o PDF, max 10 MB. Visibili solo all'amministratore.</p>
        {files.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">Nessun allegato</div>}
        <div className="grid grid-cols-2 gap-3">
          {files.map((f) => (
            <div key={f.id} className="rounded-md border border-slate-200 overflow-hidden" data-testid={`receipt-${f.id}`}>
              {f.content_type.startsWith("image/") && urls[f.id] && !f.content_type.includes("hei")
                ? <a href={urls[f.id]} target="_blank" rel="noreferrer"><img src={urls[f.id]} alt={f.original_filename} className="w-full h-32 object-cover" /></a>
                : <div className="h-32 flex items-center justify-center bg-slate-50 text-slate-400"><FileText size={28} /></div>}
              <div className="flex items-center gap-1 px-2 py-1.5 text-[11px]">
                <span className="truncate flex-1" title={f.original_filename}>{f.original_filename}</span>
                {urls[f.id] && <a data-testid={`receipt-dl-${f.id}`} href={urls[f.id]} download={f.original_filename} className="p-1 text-slate-400 hover:text-sky-600"><Download size={13} /></a>}
                <button data-testid={`receipt-del-${f.id}`} onClick={() => remove(f)} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
